import { memo, useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Collapse,
  GlobalStyles,
  Slider,
  TextField,
  MenuItem,
  Select,
  Chip,
} from '@mui/material';
import { Iconify } from 'src/components';
import AIActionPreviewModal from './AIActionPreviewModal';

// Design tokens
const colors = {
  primary: { main: '#00A76F', light: '#5BE49B', lighter: '#C8FAD6', dark: '#007867' },
  secondary: { main: '#8E33FF', lighter: '#EFD6FF' },
  success: { main: '#22C55E', light: '#77ED8B', lighter: '#D3FCD2', dark: '#118D57' },
  warning: { main: '#FFAB00', light: '#FFD666', lighter: '#FFF5CC', dark: '#B76E00' },
  error: { main: '#FF5630', light: '#FFAC82', lighter: '#FFE9D5', dark: '#B71D18' },
  info: { main: '#00B8D9', light: '#61F3F3', lighter: '#CAFDF5', dark: '#006C9C' },
  grey: { 100: '#F4F6F8', 200: '#DFE3E8', 300: '#C4CDD5', 500: '#637381', 700: '#212B36' },
};

const formatDuration = (seconds) => {
  if (!seconds) return '0m 0s';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const formatTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const getCompletenessMessage = (percentage) => {
  if (percentage >= 80) return 'Excellent!';
  if (percentage >= 50) return 'Good progress';
  return 'Needs attention';
};

// Helper to get percentage color based on required fields and completion
const getPercentageColor = (hasRequiredMissing, percentage) => {
  if (hasRequiredMissing) return colors.error.dark;
  if (percentage === 100) return colors.success.dark;
  return colors.warning.dark;
};

// Helper to get progress bar gradient based on required fields and completion
const getProgressGradient = (hasRequiredMissing, percentage) => {
  if (hasRequiredMissing) return `linear-gradient(90deg, ${colors.error.main}, ${colors.error.dark})`;
  if (percentage === 100) return `linear-gradient(90deg, ${colors.success.main}, ${colors.success.dark})`;
  return `linear-gradient(90deg, ${colors.warning.main}, ${colors.warning.dark})`;
};

// Highlight keywords in AI Summary text - enhanced with dynamic keywords
const highlightKeywords = (text, dynamicKeywords = []) => {
  // Base keywords for highlighting
  const baseKeywords = [
    'highly motivated', 'ready to proceed', 'strong interest', 'high intent',
    'finance options', 'payment', 'budget', 'urgent', 'timeline',
    'comparing options', 'research', 'booking', 'consultation',
    'impressed', 'recommended', 'as soon as possible', 'pain', 'discomfort',
    'interested', 'inquiry', 'follow-up', 'appointment'
  ];

  // Combine base keywords with dynamic keywords (patient name, treatment names)
  const allKeywords = [...baseKeywords, ...dynamicKeywords.filter(k => k && k.length > 2)];

  let result = text;
  allKeywords.forEach(keyword => {
    const regex = new RegExp(`(${keyword})`, 'gi');
    result = result.replace(regex, '|||$1|||');
  });

  return result.split('|||').map((part, i) => {
    const isKeyword = allKeywords.some(k => k.toLowerCase() === part.toLowerCase());
    if (isKeyword) {
      return (
        <Typography
          key={i}
          component="span"
          sx={{
            color: colors.secondary.main,
            fontWeight: 700,
            borderBottom: `2px solid ${colors.secondary.main}`,
          }}
        >
          {part}
        </Typography>
      );
    }
    return part;
  });
};

// AI Loading Steps Configuration
const AI_LOADING_STEPS = [
  { text: 'Initializing AI analysis engine', icon: 'mdi:robot' },
  { text: 'Processing call script responses', icon: 'mdi:file-document-outline' },
  { text: 'Extracting key conversation insights', icon: 'mdi:lightbulb-on' },
  { text: 'Analyzing patient sentiment patterns', icon: 'mdi:emoticon-happy-outline' },
  { text: 'Identifying treatment interests', icon: 'mdi:medical-bag' },
  { text: 'Evaluating budget indicators', icon: 'mdi:currency-gbp' },
  { text: 'Calculating lead readiness score', icon: 'mdi:speedometer' },
  { text: 'Detecting urgency signals', icon: 'mdi:clock-alert-outline' },
  { text: 'Generating personalized recommendations', icon: 'mdi:auto-fix' },
  { text: 'Classifying lead priority level', icon: 'mdi:star' },
  { text: 'Compiling action items', icon: 'mdi:clipboard-check-outline' },
  { text: 'Finalizing AI summary report', icon: 'mdi:file-check' },
];

// Helper function to get step icon background color (light theme)
const getStepIconBgColor = (isActive, isCompleted) => {
  if (isActive) return colors.secondary.main;
  if (isCompleted) return colors.success.main;
  return colors.grey[200];
};

// Helper function to get step text color (light theme)
const getStepTextColor = (isActive, isCompleted) => {
  if (isActive) return colors.grey[700];
  if (isCompleted) return colors.grey[500];
  return colors.grey[400];
};

// Animated Loading Screen Component - Light Theme (Fixed Layout)
const AILoadingScreen = memo(() => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);

  useEffect(() => {
    const stepDuration = 2500; // 2.5s per step for slower animation
    const interval = setInterval(() => {
      setCurrentStep(prev => {
        const next = (prev + 1) % AI_LOADING_STEPS.length;
        if (next === 0) {
          setCompletedSteps([]);
        }
        return next;
      });
      setCompletedSteps(prev => [...prev, currentStep]);
    }, stepDuration);

    return () => clearInterval(interval);
  }, [currentStep]);

  // Always show exactly 5 steps - fill with completed steps from the beginning
  const visibleSteps = AI_LOADING_STEPS.slice(0, Math.min(currentStep + 1, 5));

  return (
    <Box sx={{
      height: '100vh',
      bgcolor: '#fff',
      background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated background pattern */}
      <Box sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '200%',
          height: '200%',
          top: '-50%',
          left: '-50%',
          background: `radial-gradient(circle, ${colors.secondary.main}08 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
          animation: 'float 30s linear infinite',
          '@keyframes float': {
            '0%': { transform: 'translate(0, 0)' },
            '100%': { transform: 'translate(60px, 60px)' },
          },
        },
      }} />

      {/* Fixed position content container */}
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: 500,
        px: 3,
      }}>
        {/* Glowing orb with brain icon - fixed size */}
        <Box sx={{
          position: 'relative',
          mb: 4,
          height: 100, // Fixed height to prevent shifting
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${colors.secondary.main}15 0%, transparent 70%)`,
            animation: 'pulse-glow 3s ease-in-out infinite',
            '@keyframes pulse-glow': {
              '0%, 100%': { transform: 'scale(1)', opacity: 0.8 },
              '50%': { transform: 'scale(1.3)', opacity: 0.4 },
            },
          },
        }}>
          <Box sx={{
            width: 88,
            height: 88,
            borderRadius: '22px',
            background: `linear-gradient(135deg, ${colors.secondary.main} 0%, #8b5cf6 50%, ${colors.info.main} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 20px 60px ${colors.secondary.main}30`,
            animation: 'float-icon 4s ease-in-out infinite',
            zIndex: 1,
            '@keyframes float-icon': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(-8px)' },
            },
          }}>
            <Iconify
              icon="mdi:brain"
              width={44}
              height={44}
              sx={{
                color: '#fff',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
              }}
            />
          </Box>
        </Box>

        {/* Title */}
        <Typography sx={{
          fontSize: '1.625rem',
          fontWeight: 800,
          background: `linear-gradient(90deg, ${colors.secondary.main} 0%, #8b5cf6 50%, ${colors.info.main} 100%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 1,
          letterSpacing: '-0.5px',
          textAlign: 'center',
        }}>
          AI Analysis in Progress
        </Typography>

        <Typography sx={{
          fontSize: '0.9375rem',
          color: colors.grey[500],
          mb: 4,
          textAlign: 'center',
        }}>
          Please wait while we process your call data
        </Typography>

        {/* Steps Container - Fixed height to prevent layout shift */}
        <Box sx={{
          width: '100%',
          minHeight: 340, // Fixed height for 5 steps
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}>
          {visibleSteps.map((step, index) => {
            const isActive = index === visibleSteps.length - 1;
            const isCompleted = completedSteps.includes(index);
            const stepIndex = AI_LOADING_STEPS.indexOf(step);

          return (
            <Box
              key={stepIndex}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                px: 2,
                mb: 1.25,
                borderRadius: '14px',
                bgcolor: isActive ? `${colors.secondary.main}10` : colors.grey[100],
                border: `1px solid ${isActive ? `${colors.secondary.main}30` : colors.grey[200]}`,
                transition: 'all 0.4s ease',
                animation: isActive ? 'slideIn 0.5s ease-out' : 'none',
                boxShadow: isActive ? `0 4px 20px ${colors.secondary.main}15` : 'none',
                '@keyframes slideIn': {
                  '0%': { opacity: 0, transform: 'translateX(-20px)' },
                  '100%': { opacity: 1, transform: 'translateX(0)' },
                },
              }}
            >
              {/* Icon */}
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                bgcolor: getStepIconBgColor(isActive, isCompleted),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.4s ease',
                flexShrink: 0,
                boxShadow: isActive ? `0 4px 12px ${colors.secondary.main}30` : 'none',
              }}>
                {isCompleted && !isActive ? (
                  <Iconify icon="mdi:check" width={22} height={22} sx={{ color: '#fff' }} />
                ) : (
                  <Iconify
                    icon={step.icon}
                    width={22}
                    height={22}
                    sx={{
                      color: isActive ? '#fff' : colors.grey[500],
                      animation: isActive ? 'pulse 1.5s ease-in-out infinite' : 'none',
                      '@keyframes pulse': {
                        '0%, 100%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.1)' },
                      },
                    }}
                  />
                )}
              </Box>

              {/* Text */}
              <Typography sx={{
                fontSize: '0.9375rem',
                fontWeight: isActive ? 600 : 500,
                color: getStepTextColor(isActive, isCompleted),
                flex: 1,
                transition: 'all 0.4s ease',
              }}>
                {step.text}
              </Typography>

              {/* Status indicator - bouncing dots */}
              {isActive && (
                <Box sx={{
                  display: 'flex',
                  gap: 0.75,
                }}>
                  {[0, 1, 2].map((dot) => (
                    <Box
                      key={dot}
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: colors.secondary.main,
                        animation: 'bounce 1.6s ease-in-out infinite',
                        animationDelay: `${dot * 0.2}s`,
                        '@keyframes bounce': {
                          '0%, 80%, 100%': { transform: 'scale(0.5)', opacity: 0.3 },
                          '40%': { transform: 'scale(1)', opacity: 1 },
                        },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
        </Box>
      </Box>

      {/* Progress bar - fixed at bottom */}
      <Box sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 4,
        bgcolor: colors.grey[200],
      }}>
        <Box sx={{
          height: '100%',
          background: `linear-gradient(90deg, ${colors.secondary.main}, ${colors.info.main})`,
          animation: 'progress 4s ease-in-out infinite',
          '@keyframes progress': {
            '0%': { width: '0%', marginLeft: '0%' },
            '50%': { width: '50%', marginLeft: '25%' },
            '100%': { width: '0%', marginLeft: '100%' },
          },
        }} />
      </Box>

      {/* Powered by AI badge - fixed at bottom center */}
      <Box sx={{
        position: 'absolute',
        bottom: 28,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2.5,
        py: 1,
        bgcolor: '#fff',
        borderRadius: '24px',
        border: `1px solid ${colors.grey[200]}`,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>
        <Iconify icon="mdi:auto-awesome" width={16} height={16} sx={{ color: colors.secondary.main }} />
        <Typography sx={{ fontSize: '0.75rem', color: colors.grey[600], fontWeight: 600 }}>
          Powered by DentalSEM AI
        </Typography>
      </Box>
    </Box>
  );
});

AILoadingScreen.displayName = 'AILoadingScreen';

// Print styles - only print modal content
const printStyles = (
  <GlobalStyles
    styles={{
      '@media print': {
        'body > *:not(.MuiDialog-root)': {
          display: 'none !important',
        },
        '.MuiDialog-root': {
          position: 'static !important',
        },
        '.MuiDialog-paper': {
          boxShadow: 'none !important',
          margin: '0 !important',
        },
        '.no-print': {
          display: 'none !important',
        },
        '.print-only': {
          display: 'block !important',
        },
      },
    }}
  />
);

// Confirmation Dialog
const ConfirmationDialog = memo(({ open, onConfirm, onCancel }) => (
  <Dialog open={open} onClose={onCancel} sx={{ zIndex: 1500 }} PaperProps={{ sx: { borderRadius: '16px', p: 3, maxWidth: 400 } }}>
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: colors.warning.lighter, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
        <Iconify icon="mdi:alert-circle-outline" width={32} height={32} sx={{ color: colors.warning.dark }} />
      </Box>
      <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: colors.grey[700], mb: 1 }}>Close without action?</Typography>
      <Typography sx={{ fontSize: '0.875rem', color: colors.grey[500], mb: 3 }}>You haven&apos;t taken any recommended actions yet.</Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button fullWidth onClick={onCancel} sx={{ py: 1.25, bgcolor: colors.grey[100], color: colors.grey[700], fontWeight: 600, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: colors.grey[200] } }}>Go Back</Button>
        <Button fullWidth onClick={onConfirm} sx={{ py: 1.25, bgcolor: colors.error.main, color: '#fff', fontWeight: 600, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: colors.error.dark } }}>Close Anyway</Button>
      </Box>
    </Box>
  </Dialog>
));

ConfirmationDialog.displayName = 'ConfirmationDialog';
ConfirmationDialog.propTypes = { open: PropTypes.bool.isRequired, onConfirm: PropTypes.func.isRequired, onCancel: PropTypes.func.isRequired };

// Insight Card - Compact Card Format
const InsightCard = memo(({ icon, label, value, color = colors.grey[500] }) => (
  <Box sx={{
    bgcolor: '#fff',
    borderRadius: '10px',
    p: 1.5,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    borderLeft: `3px solid ${color}`,
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
  }}>
    <Box sx={{
      width: 38,
      height: 38,
      borderRadius: '10px',
      bgcolor: `${color}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}>
      <Iconify icon={icon} width={20} height={20} sx={{ color }} />
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, color: colors.grey[500], textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.25 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: colors.grey[700], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</Typography>
    </Box>
  </Box>
));

InsightCard.displayName = 'InsightCard';
InsightCard.propTypes = { icon: PropTypes.string.isRequired, label: PropTypes.string.isRequired, value: PropTypes.string.isRequired, color: PropTypes.string };

// Helper function to render edit input based on type
const renderEditInput = (type, value, onChange, color, options = []) => {
  if (type === 'slider') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Slider
          value={parseInt(value, 10) || 50}
          onChange={(e, newValue) => onChange(newValue)}
          min={0}
          max={100}
          size="small"
          sx={{ color, flex: 1 }}
        />
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: colors.grey[700], minWidth: 40 }}>{value}/100</Typography>
      </Box>
    );
  }
  if (type === 'select') {
    return (
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
        sx={{ fontSize: '0.875rem' }}
      >
        {options.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </Select>
    );
  }
  return (
    <TextField
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      fullWidth
      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 } }}
    />
  );
};

// Editable Insight Card - with edit button and input fields
const EditableInsightCard = memo(({ icon, label, value, color = colors.grey[500], isEditing, onEdit, onChange, type = 'text', options = [] }) => (
  <Box sx={{
    bgcolor: '#fff',
    borderRadius: '10px',
    p: 1.5,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    borderLeft: `3px solid ${color}`,
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
  }}>
    <Box sx={{
      width: 36,
      height: 36,
      borderRadius: '10px',
      bgcolor: `${color}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}>
      <Iconify icon={icon} width={20} height={20} sx={{ color }} />
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, color: colors.grey[500], textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.25 }}>{label}</Typography>
      {isEditing ? (
        renderEditInput(type, value, onChange, color, options)
      ) : (
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: colors.grey[700], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</Typography>
      )}
    </Box>
    <IconButton size="small" onClick={onEdit} sx={{ p: 0.5 }}>
      <Iconify icon={isEditing ? 'mdi:check' : 'mdi:pencil'} width={16} height={16} sx={{ color: isEditing ? colors.success.main : colors.grey[500] }} />
    </IconButton>
  </Box>
));

EditableInsightCard.displayName = 'EditableInsightCard';
EditableInsightCard.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  color: PropTypes.string,
  isEditing: PropTypes.bool,
  onEdit: PropTypes.func,
  onChange: PropTypes.func,
  type: PropTypes.oneOf(['text', 'slider', 'select']),
  options: PropTypes.array,
};

// Lead Status options
const LEAD_STATUS_OPTIONS = [
  { value: 'Hot Lead', label: '🔥 Hot Lead', emoji: '🔥' },
  { value: 'Warm Lead', label: '🌡️ Warm Lead', emoji: '🌡️' },
  { value: 'Cold Lead', label: '❄️ Cold Lead', emoji: '❄️' },
  { value: 'Not Interested', label: '⛔ Not Interested', emoji: '⛔' },
];

// WhatsApp color constants
const whatsappColors = {
  main: '#25D366',
  dark: '#128C7E',
  lighter: '#DCF8C6',
};

// Action Card (Prominent) - Updated with flexible action types
const ActionCard = memo(({ icon, title, description, priority, actionType, buttonLabel, buttonIcon, completed, completedLabel, onAction }) => {
  // Determine colors based on actionType or priority
  let bgColor;
  if (actionType === 'whatsapp') {
    bgColor = whatsappColors;
  } else if (actionType === 'email') {
    bgColor = colors.info;
  } else if (actionType === 'reminder') {
    bgColor = colors.warning;
  } else if (priority === 'high') {
    bgColor = colors.success;
  } else {
    bgColor = colors.info;
  }

  // Default button config - use function to avoid nested ternary
  const getDefaultIcon = () => {
    if (actionType === 'reminder') return 'mdi:bell-plus';
    if (priority === 'high') return 'mdi:plus';
    return 'mdi:eye';
  };
  const defaultIcon = getDefaultIcon();
  const defaultLabel = priority === 'high' ? 'Book Now' : 'Preview and Send';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: bgColor.lighter, borderRadius: '12px', border: `1px solid ${bgColor.main}20` }}>
      <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: bgColor.main, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Iconify icon={icon} width={20} height={20} sx={{ color: '#fff' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: bgColor.dark }}>{title}</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: colors.grey[500], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{description}</Typography>
      </Box>
      {completed ? (
        <Chip
          label={completedLabel || 'Done'}
          size="small"
          sx={{ bgcolor: colors.success.lighter, color: colors.success.dark, fontWeight: 600, fontSize: '0.6875rem' }}
          icon={<Iconify icon="mdi:check-circle" width={14} height={14} />}
        />
      ) : (
        <Button onClick={onAction} sx={{ py: 1, px: 2, bgcolor: bgColor.main, color: '#fff', fontSize: '0.8125rem', fontWeight: 700, borderRadius: '8px', textTransform: 'none', boxShadow: `0 4px 12px ${bgColor.main}40`, '&:hover': { bgcolor: bgColor.dark }, flexShrink: 0 }}>
          <Iconify icon={buttonIcon || defaultIcon} width={16} height={16} sx={{ mr: 0.5 }} />
          {buttonLabel || defaultLabel}
        </Button>
      )}
    </Box>
  );
});

ActionCard.displayName = 'ActionCard';
ActionCard.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  priority: PropTypes.string,
  actionType: PropTypes.string,
  buttonLabel: PropTypes.string,
  buttonIcon: PropTypes.string,
  completed: PropTypes.bool,
  completedLabel: PropTypes.string,
  onAction: PropTypes.func,
};

// Tag Component with Add Button
const Tag = memo(({ label, type, showAdd = false, onAdd }) => {
  const styles = {
    treatment: { bg: colors.info.lighter, color: colors.info.dark, icon: 'mdi:medical-bag' },
    source: { bg: colors.secondary.lighter, color: colors.secondary.main, icon: 'mdi:magnify' },
    payment: { bg: colors.warning.lighter, color: colors.warning.dark, icon: 'mdi:credit-card' },
    urgency: { bg: colors.error.lighter, color: colors.error.dark, icon: 'mdi:alert' },
  };
  const s = styles[type] || styles.treatment;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, py: 0.5, px: 1, bgcolor: s.bg, color: s.color, borderRadius: '6px', fontSize: '0.6875rem', fontWeight: 600 }}>
      <Iconify icon={s.icon} width={12} height={12} />
      {label}
      {showAdd && (
        <Box
          component="button"
          onClick={(e) => { e.stopPropagation(); onAdd?.(); }}
          sx={{
            ml: 0.5, p: 0, border: 'none', bgcolor: 'transparent', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 16, height: 16, borderRadius: '4px',
            '&:hover': { bgcolor: `${s.color}20` },
          }}
        >
          <Iconify icon="mdi:plus-circle" width={14} height={14} sx={{ color: s.color }} />
        </Box>
      )}
    </Box>
  );
});

Tag.displayName = 'Tag';
Tag.propTypes = { label: PropTypes.string.isRequired, type: PropTypes.string.isRequired, showAdd: PropTypes.bool, onAdd: PropTypes.func };

// Collapsible Section
const CollapsibleSection = memo(({ title, icon, count, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ bgcolor: colors.grey[100], borderRadius: '10px', overflow: 'hidden' }}>
      <Box onClick={() => setOpen(!open)} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: colors.grey[200] } }}>
        <Iconify icon={icon} width={18} height={18} sx={{ color: colors.grey[500] }} />
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: colors.grey[700], flex: 1 }}>{title}</Typography>
        {count > 0 && <Box sx={{ px: 1, py: 0.25, bgcolor: colors.grey[300], borderRadius: '10px', fontSize: '0.6875rem', fontWeight: 600, color: colors.grey[700] }}>{count}</Box>}
        <Iconify icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={20} height={20} sx={{ color: colors.grey[500] }} />
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 1.5, pb: 1.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
});

CollapsibleSection.displayName = 'CollapsibleSection';
CollapsibleSection.propTypes = { title: PropTypes.string.isRequired, icon: PropTypes.string.isRequired, count: PropTypes.number, children: PropTypes.node, defaultOpen: PropTypes.bool };

// Call Info Chip
const CallInfoChip = memo(({ icon, label, color = colors.grey[500] }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, py: 0.5, px: 1, bgcolor: `${color}10`, borderRadius: '6px' }}>
    <Iconify icon={icon} width={14} height={14} sx={{ color }} />
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color }}>{label}</Typography>
  </Box>
));

CallInfoChip.displayName = 'CallInfoChip';
CallInfoChip.propTypes = { icon: PropTypes.string.isRequired, label: PropTypes.string.isRequired, color: PropTypes.string };

// Input types that count as actual fields (not say-only)
const INPUT_TYPES = ['text', 'textarea', 'select', 'date', 'number', 'phone', 'email', 'choice', 'scale', 'interest'];

// Helper to check if a field has a value
const hasFieldValue = (value) => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

// Main Modal
const AISummaryModal = memo(({
  open,
  onBackToScript,
  onCloseAll,
  isLoading = false,
  summaryData = null,
  leadName = 'Patient',
  leadInitials = '?',
  callType = 'inbound',
  callStartTime,
  callDuration,
  dataCompleteness = { total: 0, filled: 0, percentage: 0 },
  formData = {},
  cardNotes = {},
  cards = [],
  onEditSkippedQuestion,
  onBookAppointment,
  onAddReminder,
  onSendWhatsApp,
  onSendEmail,
  appointmentBooked = false,
  bookedAppointmentData = null,
  leadInfo = null,
  whatsappTemplates = [],
  emailTemplates = [],
  onSaveTags,
  onSaveAINotes,
  viewMode = false, // When true, shows saved AI summary in read-only mode
  savedAIData = null, // Pre-loaded AI data for view mode
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Edit state for AI-generated fields
  const [editingField, setEditingField] = useState(null); // 'status', 'readiness', 'budget', 'availability'
  const [editedValues, setEditedValues] = useState({});
  const [customTags, setCustomTags] = useState([]);
  const [addedAITags, setAddedAITags] = useState([]); // Track which AI-suggested tags user has explicitly added
  const [newTagInput, setNewTagInput] = useState('');

  // Action completion tracking
  const [reminderAdded, setReminderAdded] = useState(false);
  const [whatsAppSent, setWhatsAppSent] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Preview modal state
  const [activePreviewModal, setActivePreviewModal] = useState(null); // 'whatsapp' | 'email' | 'reminder' | null
  const [actionSending, setActionSending] = useState(false);

  // Check if AI recommended templates match available templates
  // Only show in "Recommended Actions" if a specific template is recommended AND it exists
  const { whatsappHasMatchingTemplate, emailHasMatchingTemplate } = useMemo(() => {
    const data = summaryData || {};

    // Check WhatsApp template
    let whatsappMatch = false;
    if (data.sendWhatsApp?.recommended && data.sendWhatsApp?.templateName) {
      const recommendedWpName = data.sendWhatsApp.templateName.toLowerCase();
      whatsappMatch = whatsappTemplates.some(t =>
        t.name?.toLowerCase().includes(recommendedWpName) ||
        recommendedWpName.includes(t.name?.toLowerCase())
      );
    }

    // Check Email template
    let emailMatch = false;
    if (data.sendEmail?.recommended && data.sendEmail?.templateName) {
      const recommendedEmailName = data.sendEmail.templateName.toLowerCase();
      emailMatch = emailTemplates.some(t =>
        (t.name?.toLowerCase().includes(recommendedEmailName) ||
        t.subject?.toLowerCase().includes(recommendedEmailName) ||
        recommendedEmailName.includes(t.name?.toLowerCase()) ||
        recommendedEmailName.includes(t.subject?.toLowerCase()))
      );
    }

    return {
      whatsappHasMatchingTemplate: whatsappMatch,
      emailHasMatchingTemplate: emailMatch,
    };
  }, [summaryData, whatsappTemplates, emailTemplates]);

  // Handlers for editing
  const handleEditField = useCallback((field) => {
    if (editingField === field) {
      setEditingField(null); // Save/close edit mode
    } else {
      setEditingField(field);
    }
  }, [editingField]);

  const handleFieldChange = useCallback((field, value) => {
    setEditedValues(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleAddCustomTag = useCallback(() => {
    if (newTagInput.trim() && !customTags.includes(newTagInput.trim())) {
      setCustomTags(prev => [...prev, newTagInput.trim()]);
      setNewTagInput('');
    }
  }, [newTagInput, customTags]);

  const handleRemoveCustomTag = useCallback((tagToRemove) => {
    setCustomTags(prev => prev.filter(tag => tag !== tagToRemove));
  }, []);

  // Handlers for AI-suggested tags (user must explicitly add them)
  const handleAddAITag = useCallback((tag) => {
    const tagKey = `${tag.label}-${tag.type}`;
    if (!addedAITags.some(t => `${t.label}-${t.type}` === tagKey)) {
      setAddedAITags(prev => [...prev, tag]);
    }
  }, [addedAITags]);

  const handleRemoveAITag = useCallback((tagToRemove) => {
    const tagKey = `${tagToRemove.label}-${tagToRemove.type}`;
    setAddedAITags(prev => prev.filter(t => `${t.label}-${t.type}` !== tagKey));
  }, []);

  const handleCloseClick = useCallback(() => setShowConfirmation(true), []);

  // Collect all tags to save (classification + AI tags + custom tags)
  const handleConfirmClose = useCallback(() => {
    setShowConfirmation(false);

    // Get the current classification (edited or AI-generated)
    const currentStatus = editedValues.status ?? summaryData?.classification;

    // Collect all tags to save
    const tagsToSave = [];

    // 1. Add classification as primary tag (Hot Lead, Warm Lead, Cold Lead, Not Interested)
    if (currentStatus) {
      tagsToSave.push({
        name: currentStatus,
        type: 'classification',
        isPrimary: true,
      });
    }

    // 2. Add only user-added AI tags (not all AI-generated tags)
    if (addedAITags && addedAITags.length > 0) {
      addedAITags.forEach(tag => {
        tagsToSave.push({
          name: tag.label,
          type: tag.type,
          isPrimary: false,
        });
      });
    }

    // 3. Add custom tags added by user
    customTags.forEach(tagName => {
      tagsToSave.push({
        name: tagName,
        type: 'custom',
        isPrimary: false,
      });
    });

    // Call onSaveTags if provided
    if (onSaveTags && tagsToSave.length > 0) {
      onSaveTags(tagsToSave);
    }

    // Build AI Notes JSON to save
    const aiNotesData = {
      AI_NOTES: true,
      version: '1.0',
      timestamp: new Date().toISOString(),
      callInfo: {
        callType,
        callStartTime: callStartTime?.toISOString(),
        callDuration,
        dataCompleteness,
      },
      leadInfo: {
        name: leadName,
        initials: leadInitials,
      },
      classification: {
        status: currentStatus,
        emoji: summaryData?.classificationEmoji,
        readinessScore: editedValues.readiness ?? summaryData?.readinessScore,
        budget: editedValues.budget ?? summaryData?.budget,
        availability: editedValues.availability ?? summaryData?.availability,
        intent: summaryData?.intent,
        intentIndicators: summaryData?.intentIndicators,
      },
      summary: {
        aiSummaryText: summaryData?.aiSummaryText,
        keyQuote: summaryData?.keyQuote,
        sentiment: summaryData?.sentiment,
        highlights: summaryData?.highlights,
      },
      concerns: summaryData?.concerns || [],
      tags: [
        // Classification tag
        ...(currentStatus ? [{ label: currentStatus, type: 'classification' }] : []),
        // Only user-added AI tags (not all AI-generated tags)
        ...addedAITags,
        // Custom tags
        ...customTags.map(t => ({ label: t, type: 'custom' })),
      ],
      recommendedActions: {
        bookAppointment: summaryData?.bookAppointment,
        sendWhatsApp: summaryData?.sendWhatsApp,
        sendEmail: summaryData?.sendEmail,
        addReminder: summaryData?.addReminder,
        nextSteps: summaryData?.nextSteps,
      },
      actionsTaken: {
        appointmentBooked,
        bookedAppointmentData,
        whatsAppSent,
        emailSent,
        reminderAdded,
      },
    };

    // Call onSaveAINotes if provided
    if (onSaveAINotes) {
      onSaveAINotes(aiNotesData);
    }

    onCloseAll?.();
  }, [
    onCloseAll, onSaveTags, onSaveAINotes, editedValues, summaryData, customTags, addedAITags,
    callType, callStartTime, callDuration, dataCompleteness, leadName, leadInitials,
    appointmentBooked, bookedAppointmentData, whatsAppSent, emailSent, reminderAdded
  ]);

  const handleCancelClose = useCallback(() => setShowConfirmation(false), []);
  const handlePrint = useCallback(() => window.print(), []);

  // Handle opening preview modals
  const handleOpenPreview = useCallback((actionType) => {
    setActivePreviewModal(actionType);
  }, []);

  const handleClosePreview = useCallback(() => {
    setActivePreviewModal(null);
    setActionSending(false);
  }, []);

  // Handle sending actions from preview modal
  const handleSendAction = useCallback((actionData) => {
    setActionSending(true);

    if (activePreviewModal === 'reminder') {
      if (onAddReminder) {
        onAddReminder(actionData);
        setReminderAdded(true);
      }
    } else if (activePreviewModal === 'whatsapp') {
      if (onSendWhatsApp) {
        onSendWhatsApp(actionData);
        setWhatsAppSent(true);
      }
    } else if (activePreviewModal === 'email') {
      if (onSendEmail) {
        onSendEmail(actionData);
        setEmailSent(true);
      }
    }

    // Close modal after short delay to show success
    setTimeout(() => {
      handleClosePreview();
    }, 500);
  }, [activePreviewModal, onAddReminder, onSendWhatsApp, onSendEmail, handleClosePreview]);

  const notesArray = Object.entries(cardNotes).filter(([, note]) => note).map(([idx, note]) => ({ idx: parseInt(idx, 10), note }));

  // Calculate incomplete cards - only cards with actual input fields that have missing values
  // Skip say-only cards (cards without input fields)
  const { incompleteArray, requiredMissing } = useMemo(() => {
    const incomplete = [];
    const requiredFields = [];

    cards.forEach((card, idx) => {
      // Check if card has any input elements
      const inputElements = card.elements?.filter(el => INPUT_TYPES.includes(el.type)) || [];

      // Skip cards that don't have any input fields (say-only cards)
      if (inputElements.length === 0) return;

      // Check for unfilled fields in this card
      const unfilledFields = inputElements.filter(el => !hasFieldValue(formData[el.field]));
      const unfilledRequiredFields = inputElements.filter(el => el.required && !hasFieldValue(formData[el.field]));

      // Only add card to incomplete if it has unfilled fields
      if (unfilledFields.length > 0) {
        incomplete.push({
          idx,
          title: card?.title || `Q${idx + 1}`,
          isRequired: unfilledRequiredFields.length > 0,
        });
      }

      // Track all required fields that are missing
      unfilledRequiredFields.forEach(el => {
        requiredFields.push({
          cardIdx: idx,
          cardTitle: card?.title || `Q${idx + 1}`,
          fieldName: el.label || el.field,
        });
      });
    });

    return { incompleteArray: incomplete, requiredMissing: requiredFields };
  }, [cards, formData]);

  const isInbound = callType === 'inbound';

  // Transform savedAIData to match expected format when in viewMode
  const viewModeData = useMemo(() => {
    if (!viewMode || !savedAIData) return null;
    return {
      classification: savedAIData.classification?.status,
      classificationEmoji: savedAIData.classification?.emoji,
      readinessScore: savedAIData.classification?.readinessScore,
      budget: savedAIData.classification?.budget,
      availability: savedAIData.classification?.availability,
      intent: savedAIData.classification?.intent,
      intentIndicators: savedAIData.classification?.intentIndicators,
      keyQuote: savedAIData.summary?.keyQuote,
      aiSummaryText: savedAIData.summary?.aiSummaryText,
      sentiment: savedAIData.summary?.sentiment,
      highlights: savedAIData.summary?.highlights,
      concerns: savedAIData.concerns || [],
      nextSteps: savedAIData.recommendedActions?.nextSteps || [],
      tags: savedAIData.tags || [],
      bookAppointment: savedAIData.recommendedActions?.bookAppointment,
      addReminder: savedAIData.recommendedActions?.addReminder || { recommended: false },
      sendWhatsApp: savedAIData.recommendedActions?.sendWhatsApp || { recommended: false },
      sendEmail: savedAIData.recommendedActions?.sendEmail || { recommended: false },
    };
  }, [viewMode, savedAIData]);

  const data = viewModeData || summaryData || {
    classification: 'Hot Lead',
    classificationEmoji: '🔥',
    readinessScore: 85,
    budget: 'Finance preferred',
    availability: 'Tue/Thu PM',
    intent: 'High',
    intentIndicators: ['Ready to proceed', 'Timeline pressure'],
    keyQuote: "I've been putting this off but I'm finally ready to do something about it.",
    aiSummaryText: 'Patient is highly motivated and ready to proceed with treatment. They have done their research and are comparing options. Strong buying signals detected with clear timeline pressure due to upcoming event.',
    concerns: [
      { title: 'Self-consciousness', description: 'Impact on confidence and social situations' },
      { title: 'Budget considerations', description: 'Interested in finance options' },
    ],
    nextSteps: [
      { action: 'Book Consultation', description: 'Earliest available slot', priority: 'high' },
      { action: 'Send Info Pack', description: 'Pricing & finance options', priority: 'medium' },
    ],
    tags: [
      { label: 'Dental Implants', type: 'treatment' },
      { label: 'Google', type: 'source' },
      { label: 'Finance', type: 'payment' },
      { label: 'Urgent', type: 'urgency' },
    ],
    addReminder: { recommended: false },
    sendWhatsApp: { recommended: false },
    sendEmail: { recommended: false },
  };

  // In viewMode, use saved actions taken state
  const viewModeAppointmentBooked = viewMode ? savedAIData?.actionsTaken?.appointmentBooked : appointmentBooked;
  const viewModeWhatsAppSent = viewMode ? savedAIData?.actionsTaken?.whatsAppSent : whatsAppSent;
  const viewModeEmailSent = viewMode ? savedAIData?.actionsTaken?.emailSent : emailSent;
  const viewModeReminderAdded = viewMode ? savedAIData?.actionsTaken?.reminderAdded : reminderAdded;
  const viewModeBookedAppointmentData = viewMode ? savedAIData?.actionsTaken?.bookedAppointmentData : bookedAppointmentData;

  // Extract dynamic keywords for highlighting (treatment names, patient name)
  const dynamicKeywords = [
    leadName,
    ...(data.tags?.filter(t => t.type === 'treatment').map(t => t.label) || []),
  ];

  // Get classification color based on lead type
  const getClassificationColor = (classification) => {
    const cl = classification?.toLowerCase() || '';
    if (cl.includes('hot')) return colors.error;
    if (cl.includes('warm')) return colors.warning;
    if (cl.includes('cold')) return colors.info;
    return colors.grey;
  };

  // Use edited status if available, otherwise use AI classification
  const currentStatus = editedValues.status ?? data.classification;
  const classificationColor = getClassificationColor(currentStatus);

  return (
    <>
      {printStyles}
      <Dialog
        open={open}
        onClose={onBackToScript}
        fullScreen
        PaperProps={{ sx: { bgcolor: colors.grey[100] } }}
        disableEscapeKeyDown={isLoading}
        sx={{ zIndex: 1400 }}
      >
        {isLoading ? (
          <AILoadingScreen />
        ) : (
          <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1.5, bgcolor: '#fff', borderBottom: `1px solid ${colors.grey[200]}`, flexShrink: 0 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: `linear-gradient(135deg, ${colors.secondary.main}, ${colors.primary.main})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Iconify icon="mdi:auto-awesome" width={22} height={22} sx={{ color: '#fff' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: colors.grey[700] }}>AI Call Summary</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <CallInfoChip
                    icon={isInbound ? 'mdi:phone-incoming' : 'mdi:phone-outgoing'}
                    label={isInbound ? 'Inbound' : 'Outbound'}
                    color={isInbound ? colors.success.main : colors.info.main}
                  />
                  <CallInfoChip icon="mdi:timer-outline" label={formatDuration(callDuration)} color={colors.grey[500]} />
                  <CallInfoChip icon="mdi:clock-outline" label={formatTime(callStartTime)} color={colors.grey[500]} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1.5, background: `linear-gradient(135deg, ${colors.secondary.lighter}, ${colors.info.lighter})`, borderRadius: '20px' }}>
                <Iconify icon="mdi:brain" width={14} height={14} sx={{ color: colors.secondary.main }} />
                <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: colors.secondary.main }}>AI Powered</Typography>
              </Box>
              <Button onClick={onBackToScript} startIcon={<Iconify icon="mdi:arrow-left" width={18} height={18} />} sx={{ color: colors.grey[700], bgcolor: colors.grey[100], fontWeight: 600, borderRadius: '8px', textTransform: 'none', '&:hover': { bgcolor: colors.grey[200] } }}>Back</Button>
              <IconButton onClick={viewMode ? onCloseAll : handleCloseClick} sx={{ color: colors.grey[500] }}><Iconify icon="mdi:close" width={22} height={22} /></IconButton>
            </Box>

            {/* Main Content - 3 Column Layout */}
            <Box sx={{ flex: 1, display: 'flex', overflow: 'auto', px: 3, py: 2, gap: 2 }}>
              {/* Column 1 - Patient Info & AI Summary */}
              <Box sx={{ flex: '0 0 26%', display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
                {/* Patient Card with Classification */}
                <Box sx={{ bgcolor: '#fff', borderRadius: '16px', p: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: colors.primary.main, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1rem', boxShadow: `0 4px 12px ${colors.primary.main}40` }}>{leadInitials}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: colors.grey[700] }}>{leadName}</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {data.tags.slice(0, 2).map((tag, i) => <Tag key={i} label={tag.label} type={tag.type} />)}
                      </Box>
                    </Box>
                  </Box>

                  {/* Classification Badge - Dynamic color based on lead type - EDITABLE */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, p: 1.5, bgcolor: classificationColor.lighter, borderRadius: '12px', border: `1px solid ${classificationColor.light}`, position: 'relative' }}>
                    <Typography sx={{ fontSize: '1.75rem', lineHeight: 1 }}>
                      {editingField === 'status'
                        ? (LEAD_STATUS_OPTIONS.find(opt => opt.value === (editedValues.status ?? data.classification))?.emoji || '🔥')
                        : (data.classificationEmoji || '🔥')}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, color: classificationColor.main, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Lead Status</Typography>
                      {editingField === 'status' ? (
                        <Select
                          value={editedValues.status ?? data.classification}
                          onChange={(e) => handleFieldChange('status', e.target.value)}
                          size="small"
                          fullWidth
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            '& .MuiSelect-select': { py: 0.5 }
                          }}
                        >
                          {LEAD_STATUS_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: classificationColor.dark }}>{editedValues.status ?? data.classification}</Typography>
                      )}
                      {data.intent && !editingField && (
                        <Typography sx={{ fontSize: '0.6875rem', color: classificationColor.main, mt: 0.25 }}>
                          Intent: {data.intent}
                        </Typography>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleEditField('status')}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        p: 0.5,
                        bgcolor: editingField === 'status' ? colors.success.lighter : 'transparent',
                        '&:hover': { bgcolor: editingField === 'status' ? colors.success.light : colors.grey[200] }
                      }}
                    >
                      <Iconify
                        icon={editingField === 'status' ? 'mdi:check' : 'mdi:pencil'}
                        width={14}
                        height={14}
                        sx={{ color: editingField === 'status' ? colors.success.main : colors.grey[500] }}
                      />
                    </IconButton>
                  </Box>
                </Box>

                {/* AI Summary - Bigger Font with Keyword Highlights */}
                <Box sx={{ bgcolor: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', flex: 1 }}>
                  <Box sx={{ height: 4, background: `linear-gradient(90deg, ${colors.secondary.main}, ${colors.info.main})` }} />
                  <Box sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '10px', background: `linear-gradient(135deg, ${colors.secondary.main}, ${colors.info.main})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Iconify icon="mdi:auto-awesome" width={18} height={18} sx={{ color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700, color: colors.secondary.main }}>AI Summary</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '1rem', lineHeight: 1.8, color: colors.grey[700] }}>
                      {highlightKeywords(data.aiSummaryText, dynamicKeywords)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Column 2 - Key Quote & Recommended Actions */}
              <Box sx={{ flex: '0 0 46%', display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
                {/* Key Quote - Enhanced styling */}
                <Box sx={{
                  background: `linear-gradient(135deg, ${colors.primary.lighter} 0%, #E8F5E9 100%)`,
                  borderRadius: '16px',
                  p: 2,
                  position: 'relative',
                  boxShadow: '0 4px 12px rgba(0, 167, 111, 0.15)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: colors.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: `0 4px 12px ${colors.primary.main}40`,
                    }}>
                      <Iconify icon="mdi:format-quote-close" width={22} height={22} sx={{ color: '#fff' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{
                        fontSize: '0.9375rem',
                        fontStyle: 'italic',
                        color: colors.grey[700],
                        lineHeight: 1.6,
                        fontWeight: 500,
                        mb: 1,
                        wordBreak: 'break-word',
                      }}>
                        &ldquo;{data.keyQuote}&rdquo;
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 20, height: 2, bgcolor: colors.primary.main, borderRadius: 1 }} />
                        <Typography sx={{ fontSize: '0.6875rem', color: colors.primary.dark, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Key Quote</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Appointment Booked Success Card - Show when appointment is booked */}
                {viewModeAppointmentBooked && viewModeBookedAppointmentData && (
                  <Box sx={{
                    bgcolor: colors.success.lighter,
                    borderRadius: '16px',
                    p: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    border: `2px solid ${colors.success.main}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '12px',
                        bgcolor: colors.success.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Iconify icon="mdi:calendar-check" width={24} height={24} sx={{ color: '#fff' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: colors.success.dark }}>
                          Appointment Booked!
                        </Typography>
                        <Typography sx={{ fontSize: '0.8125rem', color: colors.success.main, fontWeight: 500 }}>
                          Successfully scheduled during this call
                        </Typography>
                      </Box>
                      <Box sx={{
                        width: 32,
                        height: 32,
                        bgcolor: colors.success.main,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Iconify icon="mdi:check-bold" width={18} height={18} sx={{ color: '#fff' }} />
                      </Box>
                    </Box>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 1.5,
                      p: 1.5,
                      bgcolor: '#fff',
                      borderRadius: '10px',
                    }}>
                      {viewModeBookedAppointmentData.datetime && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Iconify icon="mdi:calendar" width={16} height={16} sx={{ color: colors.grey[500] }} />
                          <Typography sx={{ fontSize: '0.8125rem', color: colors.grey[700], fontWeight: 500 }}>
                            {viewModeBookedAppointmentData.datetime}
                          </Typography>
                        </Box>
                      )}
                      {viewModeBookedAppointmentData.treatment && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Iconify icon="mdi:tooth" width={16} height={16} sx={{ color: colors.grey[500] }} />
                          <Typography sx={{ fontSize: '0.8125rem', color: colors.grey[700], fontWeight: 500 }}>
                            {viewModeBookedAppointmentData.treatment}
                          </Typography>
                        </Box>
                      )}
                      {viewModeBookedAppointmentData.method && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Iconify icon="mdi:map-marker" width={16} height={16} sx={{ color: colors.grey[500] }} />
                          <Typography sx={{ fontSize: '0.8125rem', color: colors.grey[700], fontWeight: 500 }}>
                            {viewModeBookedAppointmentData.method}
                          </Typography>
                        </Box>
                      )}
                      {viewModeBookedAppointmentData.type && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Iconify icon="mdi:clipboard-text" width={16} height={16} sx={{ color: colors.grey[500] }} />
                          <Typography sx={{ fontSize: '0.8125rem', color: colors.grey[700], fontWeight: 500 }}>
                            {viewModeBookedAppointmentData.type}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Recommended Actions */}
                <Box sx={{ bgcolor: '#fff', borderRadius: '16px', p: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Iconify icon="mdi:rocket-launch" width={20} height={20} sx={{ color: colors.success.main }} />
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: colors.grey[700] }}>Recommended Actions</Typography>
                    <Box sx={{ ml: 'auto', px: 1, py: 0.25, bgcolor: colors.success.lighter, borderRadius: '6px', fontSize: '0.6875rem', fontWeight: 600, color: colors.success.dark }}>Priority</Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Primary Action - Book Appointment (if recommended) */}
                    {data.bookAppointment?.recommended && !viewModeAppointmentBooked && (
                      <ActionCard
                        icon="mdi:calendar-check"
                        title="Book Appointment"
                        description={data.bookAppointment.notes || 'Schedule consultation based on call'}
                        priority="high"
                        buttonLabel="Book Now"
                        buttonIcon="mdi:plus"
                        onAction={onBookAppointment}
                      />
                    )}

                    {/* WhatsApp Action - Show in Recommended ONLY if AI recommends a specific template that exists */}
                    {data.sendWhatsApp?.recommended && whatsappHasMatchingTemplate && (
                      <ActionCard
                        icon="mdi:whatsapp"
                        title="Send WhatsApp"
                        description={`Template: ${data.sendWhatsApp.templateName}${data.sendWhatsApp.reason ? ` - ${data.sendWhatsApp.reason}` : ''}`}
                        actionType="whatsapp"
                        completed={viewModeWhatsAppSent}
                        completedLabel="Sent"
                        onAction={() => handleOpenPreview('whatsapp')}
                      />
                    )}

                    {/* Email Action - Show in Recommended ONLY if AI recommends a specific template that exists */}
                    {data.sendEmail?.recommended && emailHasMatchingTemplate && (
                      <ActionCard
                        icon="mdi:email-fast"
                        title="Send Email"
                        description={`Template: ${data.sendEmail.templateName}${data.sendEmail.reason ? ` - ${data.sendEmail.reason}` : ''}`}
                        actionType="email"
                        completed={viewModeEmailSent}
                        completedLabel="Sent"
                        onAction={() => handleOpenPreview('email')}
                      />
                    )}

                    {/* Follow-up Reminder - Show if AI recommends */}
                    {data.addReminder?.recommended && (
                      <ActionCard
                        icon="mdi:bell-ring"
                        title="Add Follow-up Reminder"
                        description={`${data.addReminder.reminderDate || 'Schedule'} - ${data.addReminder.reminderNote || 'Follow up with patient'}`}
                        actionType="reminder"
                        completed={viewModeReminderAdded}
                        completedLabel="Added"
                        onAction={() => handleOpenPreview('reminder')}
                      />
                    )}

                    {/* Other next steps from AI (non-specific actions) */}
                    {data.nextSteps?.filter(step =>
                      !step.action?.toLowerCase().includes('book') &&
                      !step.action?.toLowerCase().includes('whatsapp') &&
                      !step.action?.toLowerCase().includes('email') &&
                      !step.action?.toLowerCase().includes('reminder')
                    ).map((step, i) => (
                      <ActionCard
                        key={i}
                        icon={step.priority === 'high' ? 'mdi:star' : 'mdi:clipboard-check'}
                        title={step.action}
                        description={step.description}
                        priority={step.priority}
                        onAction={() => {
                          // Handle other actions if needed
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Patient Concerns */}
                <Box sx={{ bgcolor: '#fff', borderRadius: '16px', p: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Iconify icon="mdi:account-alert" width={20} height={20} sx={{ color: colors.warning.main }} />
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: colors.grey[700] }}>Patient Concerns</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {data.concerns.map((c, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1.5, p: 1.5, bgcolor: colors.grey[100], borderRadius: '10px' }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: colors.warning.lighter, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Iconify icon={i === 0 ? 'mdi:emoticon-sad-outline' : 'mdi:cash'} width={18} height={18} sx={{ color: colors.warning.dark }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.grey[700] }}>{c.title}</Typography>
                          <Typography sx={{ fontSize: '0.8125rem', color: colors.grey[500] }}>{c.description}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Quick Actions - Show optional actions that weren't AI recommended */}
                {((!data.bookAppointment?.recommended && !viewModeAppointmentBooked) ||
                  (data.sendWhatsApp?.recommended && !whatsappHasMatchingTemplate) ||
                  (data.sendEmail?.recommended && !emailHasMatchingTemplate)) && (
                  <Box sx={{ bgcolor: '#fff', borderRadius: '16px', p: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Iconify icon="mdi:lightning-bolt" width={20} height={20} sx={{ color: colors.info.main }} />
                      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: colors.grey[700] }}>Quick Actions</Typography>
                      <Box sx={{ ml: 'auto', px: 1, py: 0.25, bgcolor: colors.grey[200], borderRadius: '6px', fontSize: '0.6875rem', fontWeight: 600, color: colors.grey[600] }}>Optional</Box>
                    </Box>
                    <Typography sx={{ fontSize: '0.8125rem', color: colors.grey[500], mb: 1.5 }}>
                      Additional actions you can take
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {/* Book Appointment - when NOT recommended by AI */}
                      {!data.bookAppointment?.recommended && !viewModeAppointmentBooked && (
                        <ActionCard
                          icon="mdi:calendar-plus"
                          title="Book Appointment"
                          description="Schedule an appointment for this patient"
                          priority="medium"
                          buttonLabel="Book"
                          buttonIcon="mdi:plus"
                          onAction={onBookAppointment}
                        />
                      )}

                      {/* WhatsApp - when recommended but no matching template */}
                      {data.sendWhatsApp?.recommended && !whatsappHasMatchingTemplate && (
                        <ActionCard
                          icon="mdi:whatsapp"
                          title="Send WhatsApp"
                          description={data.sendWhatsApp.reason || 'Send a follow-up message via WhatsApp'}
                          actionType="whatsapp"
                          completed={viewModeWhatsAppSent}
                          completedLabel="Sent"
                          onAction={() => handleOpenPreview('whatsapp')}
                        />
                      )}

                      {/* Email - when recommended but no matching template */}
                      {data.sendEmail?.recommended && !emailHasMatchingTemplate && (
                        <ActionCard
                          icon="mdi:email-fast"
                          title="Send Email"
                          description={data.sendEmail.reason || 'Send a follow-up email'}
                          actionType="email"
                          completed={viewModeEmailSent}
                          completedLabel="Sent"
                          onAction={() => handleOpenPreview('email')}
                        />
                      )}
                    </Box>
                  </Box>
                )}

              </Box>

              {/* Column 3 - Key Insights, Auto Tags & Data Completeness (Scrollable) */}
              <Box sx={{ flex: '1 1 28%', display: 'flex', flexDirection: 'column', gap: 1.5, overflow: 'auto', minWidth: 0 }}>
                {/* Key Insights - Editable Card Format */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <EditableInsightCard
                    icon="mdi:speedometer"
                    label="Readiness"
                    value={editedValues.readiness ?? data.readinessScore}
                    color={colors.success.main}
                    isEditing={editingField === 'readiness'}
                    onEdit={() => handleEditField('readiness')}
                    onChange={(val) => handleFieldChange('readiness', val)}
                    type="slider"
                  />
                  <EditableInsightCard
                    icon="mdi:credit-card"
                    label="Budget"
                    value={editedValues.budget ?? data.budget ?? 'Not discussed'}
                    color={colors.warning.main}
                    isEditing={editingField === 'budget'}
                    onEdit={() => handleEditField('budget')}
                    onChange={(val) => handleFieldChange('budget', val)}
                    type="text"
                  />
                  <EditableInsightCard
                    icon="mdi:calendar-clock"
                    label="Available"
                    value={editedValues.availability ?? data.availability ?? 'Not specified'}
                    color={colors.info.main}
                    isEditing={editingField === 'availability'}
                    onEdit={() => handleEditField('availability')}
                    onChange={(val) => handleFieldChange('availability', val)}
                    type="text"
                  />
                </Box>

                {/* Auto Tags with Add Custom Tag */}
                <Box sx={{ bgcolor: '#fff', borderRadius: '10px', p: 1.5, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Iconify icon="mdi:tag-multiple" width={16} height={16} sx={{ color: colors.secondary.main }} />
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: colors.grey[700] }}>Auto Tags</Typography>
                    <Typography sx={{ ml: 'auto', fontSize: '0.625rem', color: colors.grey[500] }}>Will be saved</Typography>
                  </Box>

                  {/* Primary Classification Tag */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, color: colors.grey[500], textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                      Lead Classification
                    </Typography>
                    <Chip
                      icon={<Iconify icon="mdi:star" width={14} height={14} />}
                      label={editedValues.status ?? data.classification}
                      size="small"
                      sx={{
                        bgcolor: classificationColor.lighter,
                        color: classificationColor.dark,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        border: `1px solid ${classificationColor.main}`,
                        '& .MuiChip-icon': { color: classificationColor.main },
                      }}
                    />
                  </Box>

                  {/* Added AI Tags (user has clicked Add on these) */}
                  {addedAITags.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, color: colors.success.main, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                        Added Tags
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {addedAITags.map((tag, i) => (
                          <Chip
                            key={`added-${i}`}
                            label={tag.label}
                            size="small"
                            onDelete={() => handleRemoveAITag(tag)}
                            sx={{
                              bgcolor: colors.success.lighter,
                              color: colors.success.dark,
                              fontSize: '0.6875rem',
                              fontWeight: 600,
                              '& .MuiChip-deleteIcon': { color: colors.success.main },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* AI Suggested Tags (user can click to add) */}
                  {data.tags && data.tags.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, color: colors.grey[500], textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                        AI Suggested (Click to add)
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {data.tags
                          .filter(tag => !addedAITags.some(added => `${added.label}-${added.type}` === `${tag.label}-${tag.type}`))
                          .map((tag, i) => (
                            <Chip
                              key={`suggested-${i}`}
                              label={tag.label}
                              size="small"
                              onClick={() => handleAddAITag(tag)}
                              deleteIcon={<Iconify icon="mdi:plus" width={14} height={14} />}
                              onDelete={() => handleAddAITag(tag)}
                              sx={{
                                bgcolor: colors.grey[200],
                                color: colors.grey[700],
                                fontSize: '0.6875rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: colors.grey[300] },
                                '& .MuiChip-deleteIcon': { color: colors.grey[700] },
                              }}
                            />
                          ))
                        }
                      </Box>
                    </Box>
                  )}

                  {/* Custom Tags */}
                  {customTags.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, color: colors.grey[500], textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                        Custom Tags
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {customTags.map((tag, i) => (
                          <Chip
                            key={`custom-${i}`}
                            label={tag}
                            size="small"
                            onDelete={() => handleRemoveCustomTag(tag)}
                            sx={{ bgcolor: colors.secondary.lighter, color: colors.secondary.main, fontSize: '0.6875rem', fontWeight: 600 }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Add custom tag input */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <TextField
                      size="small"
                      placeholder="Add custom tag..."
                      value={newTagInput}
                      onChange={(e) => setNewTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddCustomTag()}
                      sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }}
                    />
                    <IconButton size="small" onClick={handleAddCustomTag} sx={{ bgcolor: colors.secondary.main, color: '#fff', '&:hover': { bgcolor: colors.secondary.dark } }}>
                      <Iconify icon="mdi:plus" width={16} height={16} />
                    </IconButton>
                  </Box>
                </Box>

                {/* Call Notes */}
                {notesArray.length > 0 && (
                  <CollapsibleSection title="Call Notes" icon="mdi:note-text" count={notesArray.length} defaultOpen>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {notesArray.map(({ idx, note }) => (
                        <Box key={idx} sx={{ fontSize: '0.75rem', color: colors.grey[500] }}>
                          <Typography component="span" sx={{ fontWeight: 600, color: colors.grey[700] }}>Q{idx + 1}: </Typography>{note}
                        </Box>
                      ))}
                    </Box>
                  </CollapsibleSection>
                )}

                {/* Data Completeness */}
                <Box sx={{ bgcolor: '#fff', borderRadius: '10px', p: 1.5, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Iconify icon="mdi:chart-donut" width={18} height={18} sx={{ color: requiredMissing.length > 0 ? colors.error.main : colors.success.main }} />
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: colors.grey[700] }}>Data Completeness</Typography>
                    <Typography sx={{ ml: 'auto', fontSize: '1rem', fontWeight: 700, color: getPercentageColor(requiredMissing.length > 0, dataCompleteness.percentage) }}>{dataCompleteness.percentage}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={dataCompleteness.percentage} sx={{ height: 6, borderRadius: 3, bgcolor: colors.grey[200], mb: 1, '& .MuiLinearProgress-bar': { borderRadius: 3, background: getProgressGradient(requiredMissing.length > 0, dataCompleteness.percentage) } }} />
                  <Typography sx={{ fontSize: '0.6875rem', color: colors.grey[500], mb: incompleteArray.length > 0 ? 1.5 : 0 }}>
                    {dataCompleteness.filled} of {dataCompleteness.total} fields • {getCompletenessMessage(dataCompleteness.percentage)}
                  </Typography>

                  {/* Required Fields - Mandatory */}
                  {requiredMissing.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Iconify icon="mdi:alert-circle" width={14} height={14} sx={{ color: colors.error.main }} />
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: colors.error.dark }}>
                          Required ({requiredMissing.length}) - must fill to save
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {incompleteArray.filter(item => item.isRequired).map(({ idx, title }) => (
                          <Box
                            key={idx}
                            onClick={() => onEditSkippedQuestion?.(idx)}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              py: 0.5,
                              px: 1.25,
                              bgcolor: colors.error.lighter,
                              borderRadius: '16px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              border: `1px solid ${colors.error.main}40`,
                              '&:hover': {
                                bgcolor: colors.error.light,
                                transform: 'scale(1.02)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              }
                            }}
                          >
                            <Iconify icon="mdi:alert-circle" width={12} height={12} sx={{ color: colors.error.dark }} />
                            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: colors.error.dark }}>{title}</Typography>
                            <Typography component="span" sx={{ fontSize: '0.5rem', color: colors.error.main, fontWeight: 700 }}>*</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Optional Fields - Can edit if needed */}
                  {incompleteArray.filter(item => !item.isRequired).length > 0 && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Iconify icon="mdi:pencil-outline" width={14} height={14} sx={{ color: colors.info.main }} />
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: colors.grey[600] }}>
                          Optional ({incompleteArray.filter(item => !item.isRequired).length}) - click to edit
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {incompleteArray.filter(item => !item.isRequired).map(({ idx, title }) => (
                          <Box
                            key={idx}
                            onClick={() => onEditSkippedQuestion?.(idx)}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              py: 0.5,
                              px: 1.25,
                              bgcolor: colors.info.lighter,
                              borderRadius: '16px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              border: `1px solid ${colors.info.main}30`,
                              '&:hover': {
                                bgcolor: colors.info.light,
                                transform: 'scale(1.02)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              }
                            }}
                          >
                            <Iconify icon="mdi:pencil" width={12} height={12} sx={{ color: colors.info.dark }} />
                            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: colors.info.dark }}>{title}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* All Complete Message */}
                  {incompleteArray.length === 0 && requiredMissing.length === 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                      <Iconify icon="mdi:check-circle" width={16} height={16} sx={{ color: colors.success.main }} />
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.success.dark }}>All questions completed!</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Footer - Different buttons for viewMode vs edit mode */}
            <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1.5, bgcolor: '#fff', borderTop: `1px solid ${colors.grey[200]}`, flexShrink: 0, position: 'relative', zIndex: 1500 }}>
              {viewMode ? (
                <>
                  {/* View Mode Footer - Just Close and Print */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Iconify icon="mdi:clock-outline" width={16} height={16} sx={{ color: colors.grey[500] }} />
                    <Typography sx={{ fontSize: '0.75rem', color: colors.grey[500] }}>
                      Saved {savedAIData?.timestamp ? new Date(savedAIData.timestamp).toLocaleDateString() : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  <Button onClick={handlePrint} startIcon={<Iconify icon="mdi:printer" width={18} height={18} />} sx={{ py: 1, px: 2, bgcolor: colors.grey[100], color: colors.grey[700], fontWeight: 600, borderRadius: '8px', textTransform: 'none', '&:hover': { bgcolor: colors.grey[200] } }}>Print</Button>
                  <Button
                    onClick={onCloseAll}
                    startIcon={<Iconify icon="mdi:close" width={18} height={18} />}
                    sx={{
                      py: 1, px: 2.5, mr: 8,
                      bgcolor: colors.primary.main,
                      color: '#fff',
                      fontWeight: 600, borderRadius: '8px', textTransform: 'none',
                      '&:hover': { bgcolor: colors.primary.dark },
                    }}
                  >
                    Close
                  </Button>
                </>
              ) : (
                <>
                  {/* Edit Mode Footer - Back, Print, Complete & Save */}
                  <Button onClick={onBackToScript} startIcon={<Iconify icon="mdi:arrow-left" width={18} height={18} />} sx={{ py: 1, px: 2, bgcolor: colors.grey[100], color: colors.grey[700], fontWeight: 600, borderRadius: '8px', textTransform: 'none', '&:hover': { bgcolor: colors.grey[200] } }}>Back to Script</Button>
                  <Button onClick={handlePrint} startIcon={<Iconify icon="mdi:printer" width={18} height={18} />} sx={{ py: 1, px: 2, bgcolor: colors.grey[100], color: colors.grey[700], fontWeight: 600, borderRadius: '8px', textTransform: 'none', '&:hover': { bgcolor: colors.grey[200] } }}>Print</Button>
                  <Box sx={{ flex: 1 }} />
                  {requiredMissing.length > 0 && (
                    <Typography sx={{ fontSize: '0.75rem', color: colors.error.main, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Iconify icon="mdi:alert" width={14} height={14} />
                      Fill required fields to save
                    </Typography>
                  )}
                  <Button
                    onClick={handleConfirmClose}
                    disabled={requiredMissing.length > 0}
                    startIcon={<Iconify icon="mdi:check-circle" width={20} height={20} />}
                    sx={{
                      py: 1.25, px: 3, mr: 8,
                      bgcolor: requiredMissing.length > 0 ? colors.grey[300] : colors.success.main,
                      color: requiredMissing.length > 0 ? colors.grey[500] : '#fff',
                      fontSize: '0.9375rem', fontWeight: 700, borderRadius: '10px', textTransform: 'none',
                      boxShadow: requiredMissing.length > 0 ? 'none' : `0 4px 14px ${colors.success.main}40`,
                      cursor: requiredMissing.length > 0 ? 'not-allowed' : 'pointer',
                      '&:hover': { bgcolor: requiredMissing.length > 0 ? colors.grey[300] : colors.success.dark },
                      '&:disabled': { bgcolor: colors.grey[300], color: colors.grey[500] }
                    }}
                  >
                    Complete & Save
                  </Button>
                </>
              )}
            </Box>
          </Box>
        )}
      </Dialog>

      <ConfirmationDialog open={showConfirmation} onConfirm={handleConfirmClose} onCancel={handleCancelClose} />

      {/* Action Preview Modal */}
      <AIActionPreviewModal
        open={!!activePreviewModal}
        onClose={handleClosePreview}
        onSend={handleSendAction}
        actionType={activePreviewModal}
        aiRecommendation={(() => {
          if (activePreviewModal === 'whatsapp') return data?.sendWhatsApp;
          if (activePreviewModal === 'email') return data?.sendEmail;
          if (activePreviewModal === 'reminder') return data?.addReminder;
          return {};
        })()}
        leadInfo={leadInfo || { first_name: leadName?.split(' ')[0], last_name: leadName?.split(' ').slice(1).join(' ') }}
        isSending={actionSending}
      />
    </>
  );
});

AISummaryModal.displayName = 'AISummaryModal';

AISummaryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onBackToScript: PropTypes.func.isRequired,
  onCloseAll: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  summaryData: PropTypes.object,
  leadName: PropTypes.string,
  leadInitials: PropTypes.string,
  callType: PropTypes.string,
  callStartTime: PropTypes.instanceOf(Date),
  callDuration: PropTypes.number,
  dataCompleteness: PropTypes.shape({ total: PropTypes.number, filled: PropTypes.number, percentage: PropTypes.number }),
  formData: PropTypes.object,
  cardNotes: PropTypes.object,
  cards: PropTypes.array,
  onEditSkippedQuestion: PropTypes.func,
  onBookAppointment: PropTypes.func,
  onAddReminder: PropTypes.func,
  onSendWhatsApp: PropTypes.func,
  onSendEmail: PropTypes.func,
  appointmentBooked: PropTypes.bool,
  bookedAppointmentData: PropTypes.shape({
    datetime: PropTypes.string,
    treatment: PropTypes.string,
    method: PropTypes.string,
    type: PropTypes.string,
  }),
  leadInfo: PropTypes.shape({
    id: PropTypes.number,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
  }),
  whatsappTemplates: PropTypes.array,
  emailTemplates: PropTypes.array,
  onSaveTags: PropTypes.func,
  onSaveAINotes: PropTypes.func,
  viewMode: PropTypes.bool,
  savedAIData: PropTypes.object,
};

export default AISummaryModal;
